import { useQuery } from "@tanstack/react-query";
import { USE_MOCK } from "@/api/client";
import { captureApi, type CaptureMe } from "@/api/endpoints/capture";

const SURFACE_HINT_KEY = "ubik_app_surface";
const SURFACE_CHOICE_KEY = "ubik_surface_choice";

/**
 * WF10 standing — shared by the sidebar gate, the office review page and the
 * app-surface branch in App.tsx, so they cannot disagree.
 *
 * Deliberately no USE_MOCK fixture (house rule: a working-looking screen
 * before a real source is wired is worse than no screen) — in mock mode the
 * query is disabled and the workflow is simply absent.
 */
export function useCaptureMe(enabled = true) {
  return useQuery<CaptureMe>({
    queryKey: ["capture", "me"],
    queryFn: captureApi.me,
    enabled: enabled && !USE_MOCK,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Last-known app surface, so a capture-only phone doesn't flash (and download)
 * the workbench while /me is in flight on later boots. A HINT for first paint
 * only — the backend response always wins, and clearing storage merely costs
 * one workbench flash, never access.
 */
export function surfaceHint(): "capture" | null {
  try {
    return localStorage.getItem(SURFACE_HINT_KEY) === "capture" ? "capture" : null;
  } catch {
    return null;
  }
}

/**
 * Seed the surface hint from a SIGN-IN response, before /capture/me has ever
 * been called on this device.
 *
 * NOT OPTIONAL on the phone + OTP path. That is the one route where the hint
 * is guaranteed absent: a fresh floor handset has never loaded /capture/me, so
 * SurfaceGate falls through to <AppRoutes /> and downloads the entire 88-page
 * workbench chunk over factory wifi before /me answers and sends it back to
 * the capture app. POST /v1/otp/verify returns `app_surface` precisely so this
 * can be avoided.
 *
 * Still only a HINT for first paint — the /capture/me response always wins,
 * and rememberSurface() corrects it moments later.
 */
export function seedSurfaceHint(surface: string | null | undefined): void {
  try {
    if (surface === "capture") {
      localStorage.setItem(SURFACE_HINT_KEY, "capture");
    } else if (surface) {
      // A known non-capture surface must CLEAR a stale hint, or a shared phone
      // that last carried an operator keeps sending a workbench user to the
      // capture app.
      localStorage.removeItem(SURFACE_HINT_KEY);
    }
    // `null`/undefined means the backend said nothing — leave the hint alone.
  } catch {
    /* blocked storage — the hint is optional */
  }
}

/**
 * Drop the hint at sign-OUT.
 *
 * Floor handsets are SHARED. Nothing else clears this: seedSurfaceHint only
 * clears on a known non-capture surface, and rememberSurface only once
 * /capture/me has answered. So an operator's "capture" hint outlived their
 * session, and the next person to sign in on that handset opened into the
 * capture shell — and stayed there for as long as /capture/me was slow or
 * unreachable, which on factory wifi is exactly when it matters.
 *
 * Clearing costs the next capture-only user one workbench flash on their first
 * sign-in. Not clearing costs an office user the whole app.
 */
export function clearSurfaceHint(): void {
  try {
    localStorage.removeItem(SURFACE_HINT_KEY);
    // The hand-picked surface goes with it, for the same reason: handsets are
    // shared, and a supervisor who chose the full app on one would otherwise
    // hand the next operator a workbench instead of a camera.
    localStorage.removeItem(SURFACE_CHOICE_KEY);
  } catch {
    /* blocked storage — the hint is optional */
  }
}

export function rememberSurface(me: CaptureMe | undefined): void {
  try {
    if (me && me.enabled && me.app_surface === "capture") {
      localStorage.setItem(SURFACE_HINT_KEY, "capture");
    } else if (me) {
      localStorage.removeItem(SURFACE_HINT_KEY);
    }
  } catch {
    /* blocked storage — the hint is optional */
  }
}


/**
 * Is this a handheld?
 *
 * SYNCHRONOUS on purpose, and not `useIsMobile`. That hook starts `false` and
 * corrects inside an effect, which is fine for a layout tweak and wrong here:
 * the surface gate picks a whole app TREE on first paint, so a hook would
 * paint the workbench on a real floor phone, download its chunk over factory
 * wifi, and only then flip to the camera -- the exact cost `surfaceHint`
 * exists to avoid.
 *
 * The PRIMARY pointer, not the width. A phone in landscape is still 844px
 * wide, so a width test sends it to the workbench the moment the operator
 * turns it sideways. A touchscreen laptop reports `fine`, because its primary
 * pointer is the trackpad.
 */
export function isHandheld(): boolean {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    // An environment that cannot answer is not a phone. Guessing "phone" here
    // would trap a desktop user in a camera flow; guessing "desktop" costs a
    // phone user one tap on "Use the capture app".
    return false;
  }
}

/** A surface the person picked by hand, which beats the device check. */
export function surfaceChoice(): "full" | "capture" | null {
  try {
    const v = localStorage.getItem(SURFACE_CHOICE_KEY);
    return v === "full" || v === "capture" ? v : null;
  } catch {
    return null;
  }
}

export function chooseSurface(surface: "full" | "capture" | null): void {
  try {
    if (surface) localStorage.setItem(SURFACE_CHOICE_KEY, surface);
    else localStorage.removeItem(SURFACE_CHOICE_KEY);
  } catch {
    /* blocked storage — the choice is optional */
  }
}

/**
 * Does this person, on this device, get the floor app?
 *
 * `app_surface: "capture"` says where this ACCOUNT lives. It has never said
 * anything about what the person is holding, and treating it as though it did
 * is what made the floor app a prison: a supervisor who signs in with the same
 * number on a laptop got a full-screen camera flow and no route to the rest of
 * Ubik. Since every Sheet Capture team member is a phone account today, that
 * meant nobody on any team could reach the workflow from a desk at all.
 *
 * Order matters: an explicit choice beats the device, and the device beats the
 * account. Nothing here grants access -- every route is still checked by its
 * own backend. This picks which tree renders.
 */
export function wantsFloorApp(
  me: Pick<CaptureMe, "enabled" | "app_surface"> | undefined,
): boolean {
  if (!me?.enabled || me.app_surface !== "capture") return false;
  return surfaceChoice() ? surfaceChoice() === "capture" : isHandheld();
}
